package nl.astraeus.midi.view

import kotlinx.browser.document
import kotlinx.html.canvas
import kotlinx.html.js.*
import kotlinx.html.style
import nl.astraeus.komp.HtmlBuilder
import nl.astraeus.komp.Komponent
import nl.astraeus.komp.currentElement
import nl.astraeus.midi.Note
import org.khronos.webgl.Uint8Array
import org.khronos.webgl.get
import org.w3c.dom.*
import org.w3c.dom.events.MouseEvent

class Keyboard(
  var channel: Int = -1,
  val lowNote: Note = Note.C4,
  val onMidi: (Uint8Array) -> Unit = {}
) : Komponent() {
  val transpose = lowNote.ordinal - Note.C4.ordinal
  var canvas = document.createElement("canvas") as HTMLCanvasElement
  var ctx = canvas.getContext("2d") as CanvasRenderingContext2D
  var playingNotes = mutableSetOf<Note>()
  var lastNote: Note? = null

  val keyLocations: Map<Note, Double> by lazy {
    val locations = mutableMapOf<Note, Double>()
    val startX = 200.0 - 12.5
    val whiteKeyWidth = 115.0
    val blackKeyWidth = 50.0

    var currentWhiteX = startX
    // Starting from C4 (MIDI 60)
    for (i in 60..84) {
      if (i >= Note.entries.size) break
      val note = Note.entries[i]
      if (!note.blackKey) {
        locations[note] = currentWhiteX + whiteKeyWidth / 2.0
        currentWhiteX += whiteKeyWidth
      }
    }

    // Now handle black keys
    currentWhiteX = startX
    for (i in 60..84) {
      if (i + 1 >= Note.entries.size) break
      val note = Note.entries[i]
      val nextNote = Note.entries[i + 1]
      if (!note.blackKey && nextNote.blackKey) {
        locations[nextNote] = currentWhiteX + whiteKeyWidth
      }
      if (!note.blackKey) {
        currentWhiteX += whiteKeyWidth
      }
    }

    locations
  }

  fun handleMidi(data: Uint8Array) {
    if (channel == -1 || data[0].toInt() and channel == channel) {
      when (data[0].toInt() and 0xf0) {
        0x90 -> {
          val note = Note.entries[data[1].toInt()]

          playingNotes.add(note)
          draw()
        }

        0x80 -> {
          val note = Note.entries[data[1].toInt()]

          playingNotes.remove(note)
          draw()
        }
      }
    }
  }

  private fun displayCoordsToCanvasCoords(
    displayX: Int,
    displayY: Int,
  ): Pair<Double, Double> {
    val rect = canvas.getBoundingClientRect()
    val scaleX = canvas.width / rect.width
    val scaleY = canvas.height / rect.height
    val x = (displayX - rect.left) * scaleX
    val y = (displayY - rect.top) * scaleY
    return x to y
  }

  private fun getMouseXY(e: MouseEvent): Pair<Double, Double> = displayCoordsToCanvasCoords(
    e.clientX,
    e.clientY,
  )

  private fun getTouchXY(e: TouchEvent): Pair<Double, Double>? {
    val touch = e.touches[0]
    return if (touch != null) {
      displayCoordsToCanvasCoords(
        touch.clientX,
        touch.clientY
      )
    } else {
      null
    }
  }

  private fun sendNoteOn(note: Note) {
    val data = Uint8Array(3)
    data.asDynamic()[0] = 0x90
    data.asDynamic()[1] = note.ordinal + transpose
    data.asDynamic()[2] = 64
    onMidi(data)
  }

  private fun sendNoteOff(note: Note) {
    val data = Uint8Array(3)
    data.asDynamic()[0] = 0x80
    data.asDynamic()[1] = note.ordinal + transpose
    data.asDynamic()[2] = 0
    onMidi(data)
    lastNote = null
  }

  private fun handlePress(x: Double, y: Double) {
    val note = findNoteAt(x, y)
    if (note != null) {
      lastNote = note
      playingNotes.add(note)
      draw()

      sendNoteOn(note)
    }
  }

  private fun handleMove(x: Double, y: Double) {
    val note = findNoteAt(x, y)
    if (note != lastNote) {
      lastNote?.let { ln ->
        playingNotes.remove(ln)
        sendNoteOff(ln)
      }

      lastNote = note

      note?.let { n ->
        playingNotes.add(n)
        sendNoteOn(n)
      }
      draw()
    }
  }

  private fun handleRelease() {
    lastNote?.let { note ->
      playingNotes.remove(note)
      draw()

      sendNoteOff(note)
    }
  }

  override fun HtmlBuilder.render() {
    canvas {
      width = "2100"
      height = "500"
      style = "width: 100%;"

      this@Keyboard.canvas = currentElement() as HTMLCanvasElement
      this@Keyboard.ctx = canvas.getContext("2d") as CanvasRenderingContext2D

      onMouseDownFunction = { e ->
        if (e is MouseEvent) {
          val (x, y) = getMouseXY(e)
          handlePress(x, y)
        }
      }

      onTouchStartFunction = { e ->
        if (e is TouchEvent) {
          getTouchXY(e)?.let { (x, y) ->
            handlePress(x, y)
          }
          e.preventDefault()
        }
      }

      onMouseUpFunction = { _ ->
        handleRelease()
      }

      onTouchEndFunction = { e ->
        handleRelease()
        if (e is TouchEvent) {
          e.preventDefault()
        }
      }

      onMouseLeaveFunction = { _ ->
        handleRelease()
      }

      onTouchCancelFunction = { e ->
        handleRelease()
        if (e is TouchEvent) {
          e.preventDefault()
        }
      }

      /*      onTouchMoveFunction = { e ->
              if (e is TouchEvent) {
                getTouchXY(e)?.let { (x, y) ->
                  handleMove(x, y)
                }
                e.preventDefault()
              }
            }*/

      draw()
    }
  }

  private fun findNoteAt(x: Double, y: Double): Note? {
    val startX = 200.0 - 12.5
    val whiteKeyWidth = 115.0
    val blackKeyWidth = 50.0

    // Check black keys first (they are on top)
    if (y <= 300.0) {
      var currentX = startX + whiteKeyWidth - blackKeyWidth / 2.0
      for (i in 0 until 14) {
        if (i % 7 != 2 && i % 7 != 6) {
          if (x >= currentX && x <= currentX + blackKeyWidth) {
            // Found a black key position, now find which note it is
            // C4 is index 60.
            // i=0: C#4 (61), i=1: D#4 (63), i=3: F#4 (66), i=4: G#4 (68), i=5: A#4 (70)
            // i=7: C#5 (73), i=8: D#5 (75), i=10: F#5 (78), i=11: G#5 (80), i=12: A#5 (82)
            val octave = i / 7
            val noteInOctave = when (i % 7) {
              0 -> 1 // C#
              1 -> 3 // D#
              3 -> 6 // F#
              4 -> 8 // G#
              5 -> 10 // A#
              else -> -1
            }
            if (noteInOctave != -1) {
              return Note.entries[60 + octave * 12 + noteInOctave]
            }
          }
        }
        currentX += whiteKeyWidth
      }
    }

    // Check white keys
    if (x >= startX && x <= startX + 15 * whiteKeyWidth) {
      val whiteIndex = ((x - startX) / whiteKeyWidth).toInt()
      if (whiteIndex in 0..15) {
        val octave = whiteIndex / 7
        val noteInOctave = when (whiteIndex % 7) {
          0 -> 0 // C
          1 -> 2 // D
          2 -> 4 // E
          3 -> 5 // F
          4 -> 7 // G
          5 -> 9 // A
          6 -> 11 // B
          else -> -1
        }
        if (noteInOctave != -1) {
          return Note.entries[60 + octave * 12 + noteInOctave]
        }
      }
    }

    return null
  }

  override fun renderUpdate() {
  }

  fun draw() {
    ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())

    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = ImageSmoothingQuality.HIGH  // LOW, MEDIUM, or HIGH

    ctx.strokeStyle = "rgba(0,0,0,0.5)"
    ctx.lineWidth = 2.0

    val startX = 200.0 - 12.5
    val whiteKeyWidth = 115.0
    val blackKeyWidth = 50.0

    var x = startX

    ctx.globalAlpha = 0.5
    ctx.beginPath()

    ctx.rect(startX, 0.5, 1725.0, 1.0)
    ctx.rect(startX, 497.5, 1725.0, 1.0)
    repeat(16) {
      ctx.rect(x, 0.5, 1.0, 497.0)
      x += whiteKeyWidth
    }

    ctx.stroke()

    ctx.fillStyle = "rgba(0,0,0,1.0)"
    ctx.strokeStyle = "rgba(0,0,0,1.0)"
    ctx.globalAlpha = 1.0
    ctx.beginPath()

    x = startX + whiteKeyWidth - blackKeyWidth / 2.0
    for (i in 0 until 14) {
      if (i % 7 != 2 && i % 7 != 6) {
        ctx.rect(x + i * whiteKeyWidth, 0.5, blackKeyWidth, 300.0)
      }
    }
    ctx.fill()

    ctx.fillStyle = "rgba(255,0,0,1.0)"
    for (note in playingNotes) {
      keyLocations[note]?.let { xLoc ->
        ctx.beginPath()
        ctx.arc(xLoc, 50.0, 20.0, 0.0, 2 * kotlin.math.PI)
        ctx.fill()
      }
    }
  }
}
